import { useCallback, useEffect, useState } from "react";
import {
  Loader2, ImagePlus, ImageOff, RefreshCw, Sparkles, Pencil, Plus, Wand2,
  Split, Combine, MoveVertical, X, AlertTriangle, ChevronUp, ChevronDown,
  Camera, MessageCircle, Users, Film, Check, Download, RotateCw,
} from "lucide-react";
import { api, apiError } from "../lib/api";
import { EditableText } from "./EditableText";
import { ConfirmModal } from "./ConfirmModal";

const SHOT_TYPES = [
  "establishing", "character_action", "reaction", "detail",
  "emotional_beat", "transition",
];

export const StoryboardEditor = ({ project, storyboard, projectId, onProjectUpdate, disabled }) => {
  const [busy, setBusy] = useState(false);
  const [batchShots, setBatchShots] = useState(new Set()); // shot ids currently generating
  const [selection, setSelection] = useState({}); // {sceneId: Set(shotIds)}
  const [error, setError] = useState("");
  const [confirmGen, setConfirmGen] = useState(false);
  const [reorderScene, setReorderScene] = useState(null); // scene obj currently in reorder mode
  const [reorderList, setReorderList] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // "webtoon" | "pdf" | null

  const doExport = async (format) => {
    setExporting(format);
    setError("");
    try {
      const res = await api.get(
        `/projects/${projectId}/storyboard/export?format=${format}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], {
        type: format === "pdf" ? "application/pdf" : "image/png",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const title = (project?.title || "chapter").toLowerCase().replace(/[^a-z0-9_-]+/g, "_");
      const chapter = storyboard?.chapter_number || 1;
      a.download = `${title}-chapter-${chapter}.${format === "pdf" ? "pdf" : "png"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setExporting(null);
    }
  };

  const generateStoryboard = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/storyboard/generate`);
      onProjectUpdate(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
      setConfirmGen(false);
    }
  };

  const patchShot = async (shotId, patch) => {
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/storyboard/shots/${shotId}`, patch,
      );
      onProjectUpdate(data);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const generateShotImage = async (shotId, upscale = false) => {
    setBatchShots((s) => new Set([...s, shotId]));
    setError("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/storyboard/shots/${shotId}/image`, { upscale },
      );
      onProjectUpdate(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBatchShots((s) => { const n = new Set(s); n.delete(shotId); return n; });
    }
  };

  const generateSceneImages = async (sceneId) => {
    setError("");
    try {
      await api.post(`/projects/${projectId}/storyboard/scenes/${sceneId}/generate-images`);
      // Poll every 5s
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}`);
          onProjectUpdate(data);
          attempts++;
          if (attempts < 40) setTimeout(poll, 5000);
        } catch { /* noop */ }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const generateAllImages = async () => {
    setError("");
    try {
      await api.post(`/projects/${projectId}/storyboard/generate-all-images`);
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}`);
          onProjectUpdate(data);
          attempts++;
          if (attempts < 60) setTimeout(poll, 6000);
        } catch { /* noop */ }
      };
      setTimeout(poll, 6000);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const retryFailed = async () => {
    setError("");
    try {
      // Optimistically mark all failed shots as generating so the UI reacts instantly
      const failedIds = [];
      for (const sc of storyboard?.scenes || []) {
        for (const beat of sc.beats || []) {
          for (const sh of beat.shots || []) {
            if (sh.image_status === "failed") failedIds.push(sh.id);
          }
        }
      }
      setBatchShots((s) => new Set([...s, ...failedIds]));
      await api.post(`/projects/${projectId}/storyboard/retry-failed-images`);
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}`);
          onProjectUpdate(data);
          // Stop polling once no failed shot remains in the "generating" state we requested
          const sb = data.story_context?.storyboard;
          const stillGenerating = (sb?.scenes || []).some((sc) =>
            (sc.beats || []).some((b) =>
              (b.shots || []).some((sh) => failedIds.includes(sh.id) && sh.image_status === "generating"),
            ),
          );
          attempts++;
          if (stillGenerating && attempts < 60) {
            setTimeout(poll, 5000);
          } else {
            setBatchShots((s) => {
              const n = new Set(s);
              failedIds.forEach((id) => n.delete(id));
              return n;
            });
          }
        } catch {
          setBatchShots((s) => {
            const n = new Set(s);
            failedIds.forEach((id) => n.delete(id));
            return n;
          });
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const addShot = async (sceneId, afterShotId = null) => {
    try {
      const { data } = await api.post(
        `/projects/${projectId}/storyboard/scenes/${sceneId}/shots`,
        { after_shot_id: afterShotId },
      );
      onProjectUpdate(data);
    } catch (e) { setError(apiError(e)); }
  };

  const splitShot = async (shotId) => {
    try {
      const { data } = await api.post(
        `/projects/${projectId}/storyboard/shots/${shotId}/split`, {},
      );
      onProjectUpdate(data);
    } catch (e) { setError(apiError(e)); }
  };

  const deleteShot = async (shotId) => {
    try {
      const { data } = await api.delete(`/projects/${projectId}/storyboard/shots/${shotId}`);
      onProjectUpdate(data);
    } catch (e) { setError(apiError(e)); }
  };

  const mergeSelected = async (sceneId) => {
    const set = selection[sceneId];
    if (!set || set.size < 2) {
      setError("Select at least 2 shots in a scene to merge.");
      return;
    }
    try {
      const { data } = await api.post(
        `/projects/${projectId}/storyboard/scenes/${sceneId}/shots/merge`,
        { shot_ids: [...set] },
      );
      onProjectUpdate(data);
      setSelection((s) => ({ ...s, [sceneId]: new Set() }));
    } catch (e) { setError(apiError(e)); }
  };

  const toggleSelect = (sceneId, shotId) => {
    setSelection((s) => {
      const cur = new Set(s[sceneId] || []);
      if (cur.has(shotId)) cur.delete(shotId); else cur.add(shotId);
      return { ...s, [sceneId]: cur };
    });
  };

  const openReorder = (scene) => {
    setReorderScene(scene);
    const flat = [];
    for (const b of scene.beats || []) {
      for (const sh of b.shots || []) flat.push(sh);
    }
    setReorderList(flat);
  };

  const moveReorder = (idx, delta) => {
    setReorderList((l) => {
      const next = [...l];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return l;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const applyReorder = async () => {
    if (!reorderScene) return;
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/storyboard/scenes/${reorderScene.id}/reorder`,
        { shot_ids: reorderList.map((s) => s.id) },
      );
      onProjectUpdate(data);
      setReorderScene(null);
    } catch (e) { setError(apiError(e)); }
  };

  // ---------- render ----------
  if (!storyboard) {
    return (
      <div className="mvp-storyboard-empty" data-testid="storyboard-empty">
        <Film size={38} />
        <h3>No storyboard yet</h3>
        <p>Break your chapter into scenes, beats and shots for true visual storytelling.</p>
        <button
          className="btn-primary mvp-cta-sm"
          onClick={() => setConfirmGen(true)}
          disabled={disabled || busy}
          data-testid="storyboard-generate-btn"
        >
          {busy ? <Loader2 size={14} className="mvp-spin" /> : <Wand2 size={14} />}
          {busy ? "Building storyboard…" : "Generate Storyboard"}
        </button>
        {error && <div className="mvp-error mvp-storyboard-err">{error}</div>}
        <ConfirmModal
          open={confirmGen}
          title="Generate storyboard?"
          message="Claude will build a Chapter → Scenes → Beats → Shots storyboard from your project. Your current storyboard (if any) will be snapshotted first."
          confirmLabel="Generate"
          testid="storyboard-generate-confirm"
          busy={busy}
          onCancel={() => setConfirmGen(false)}
          onConfirm={generateStoryboard}
        />
      </div>
    );
  }

  const scenes = storyboard.scenes || [];
  const failedCount = scenes.reduce(
    (n, sc) => n + (sc.beats || []).reduce(
      (m, b) => m + (b.shots || []).filter((sh) => sh.image_status === "failed").length,
      0,
    ),
    0,
  );

  return (
    <div className="mvp-storyboard" data-testid="storyboard-view">
      <div className="mvp-storyboard-head">
        <div>
          <div className="mvp-storyboard-chapter">
            Chapter {storyboard.chapter_number || 1}: {storyboard.chapter_title}
          </div>
          {storyboard.chapter_summary && (
            <p className="mvp-dim">{storyboard.chapter_summary}</p>
          )}
        </div>
        <div className="mvp-storyboard-actions">
          <button
            className="mvp-nav-ghost"
            onClick={() => setConfirmGen(true)}
            disabled={disabled || busy}
            data-testid="storyboard-regenerate-btn"
          >
            <Wand2 size={14} /> Regenerate Storyboard
          </button>
          <button
            className="mvp-nav-ghost"
            onClick={() => setExportOpen(true)}
            disabled={disabled}
            data-testid="storyboard-export-btn"
          >
            <Download size={14} /> Export Chapter
          </button>
          {failedCount > 0 && (
            <button
              className="mvp-nav-ghost mvp-retry-btn"
              onClick={retryFailed}
              disabled={disabled}
              data-testid="storyboard-retry-failed-btn"
            >
              <RotateCw size={14} /> Retry Failed ({failedCount})
            </button>
          )}
          <button
            className="btn-primary mvp-cta-sm"
            onClick={generateAllImages}
            disabled={disabled}
            data-testid="storyboard-generate-all-images-btn"
          >
            <ImagePlus size={14} /> Generate All Images
          </button>
        </div>
      </div>
      {error && <div className="mvp-error mvp-storyboard-err" data-testid="storyboard-error">{error}</div>}

      {scenes.map((scene, si) => (
        <SceneBlock
          key={scene.id}
          scene={scene}
          sceneIndex={si}
          project={project}
          disabled={disabled}
          batchShots={batchShots}
          selection={selection[scene.id] || new Set()}
          onToggleSelect={(shotId) => toggleSelect(scene.id, shotId)}
          onPatchShot={patchShot}
          onGenerateShot={generateShotImage}
          onDeleteShot={deleteShot}
          onSplitShot={splitShot}
          onAddShot={(afterId) => addShot(scene.id, afterId)}
          onGenerateSceneImages={() => generateSceneImages(scene.id)}
          onMerge={() => mergeSelected(scene.id)}
          onReorder={() => openReorder(scene)}
        />
      ))}

      <ConfirmModal
        open={confirmGen}
        title="Regenerate storyboard?"
        message="Claude will build a fresh storyboard. Your current one will be snapshotted to Version History first so you can restore it."
        confirmLabel="Regenerate"
        testid="storyboard-generate-confirm"
        busy={busy}
        onCancel={() => setConfirmGen(false)}
        onConfirm={generateStoryboard}
      />

      {reorderScene && (
        <ReorderModal
          scene={reorderScene}
          list={reorderList}
          onMove={moveReorder}
          onApply={applyReorder}
          onCancel={() => setReorderScene(null)}
        />
      )}

      {exportOpen && (
        <div className="mvp-modal-backdrop" onClick={exporting ? undefined : () => setExportOpen(false)}>
          <div className="mvp-modal mvp-modal-wide" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
            <button className="mvp-modal-close" onClick={() => setExportOpen(false)} disabled={!!exporting}>
              <X size={16} />
            </button>
            <div className="mvp-modal-icon"><Download size={22} /></div>
            <h3>Export Chapter</h3>
            <p>Download your storyboard as a scrollable long image or a print-ready PDF. Dialogue captions and shot metadata are baked in.</p>
            <div className="mvp-export-choices">
              <button
                className="mvp-export-choice"
                onClick={() => doExport("webtoon")}
                disabled={!!exporting}
                data-testid="export-webtoon-btn"
              >
                {exporting === "webtoon" ? <Loader2 size={22} className="mvp-spin" /> : <ImagePlus size={22} />}
                <span className="mvp-export-choice-title">Webtoon PNG</span>
                <span className="mvp-export-choice-sub">One tall scrollable image · social-ready</span>
              </button>
              <button
                className="mvp-export-choice"
                onClick={() => doExport("pdf")}
                disabled={!!exporting}
                data-testid="export-pdf-btn"
              >
                {exporting === "pdf" ? <Loader2 size={22} className="mvp-spin" /> : <Film size={22} />}
                <span className="mvp-export-choice-title">Multi-page PDF</span>
                <span className="mvp-export-choice-sub">One shot per page · print-ready</span>
              </button>
            </div>
            {exporting && (
              <p className="mvp-dim mvp-export-hint">Compositing panels with dialogue overlays… may take 10-30s.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =================== Scene block ===================
const SceneBlock = ({
  scene, sceneIndex, project, disabled,
  batchShots, selection,
  onToggleSelect, onPatchShot, onGenerateShot, onDeleteShot, onSplitShot, onAddShot,
  onGenerateSceneImages, onMerge, onReorder,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const shotCount = (scene.beats || []).reduce((n, b) => n + (b.shots?.length || 0), 0);

  return (
    <section className="mvp-scene-block" data-testid={`scene-${scene.id}`}>
      <header className="mvp-scene-head">
        <button
          className="mvp-icon-btn"
          onClick={() => setCollapsed((c) => !c)}
          data-testid={`scene-toggle-${scene.id}`}
          aria-label="Toggle scene"
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
        <div className="mvp-scene-meta">
          <div className="mvp-scene-title-row">
            <span className="mvp-scene-num">Scene {sceneIndex + 1}</span>
            <h3 data-testid={`scene-title-${scene.id}`}>{scene.title || "Untitled scene"}</h3>
          </div>
          <div className="mvp-scene-badges">
            {scene.location && <span className="mvp-chip">{scene.location}</span>}
            {scene.time_of_day && <span className="mvp-chip">{scene.time_of_day}</span>}
            {scene.mood && <span className="mvp-chip">{scene.mood}</span>}
            <span className="mvp-dim">{shotCount} shots</span>
          </div>
        </div>
        <div className="mvp-scene-actions">
          <button
            className="mvp-nav-ghost"
            onClick={onReorder}
            disabled={disabled || shotCount < 2}
            data-testid={`scene-reorder-${scene.id}`}
          >
            <MoveVertical size={13} /> Reorder
          </button>
          <button
            className="mvp-nav-ghost"
            onClick={onMerge}
            disabled={disabled || selection.size < 2}
            data-testid={`scene-merge-${scene.id}`}
          >
            <Combine size={13} /> Merge ({selection.size})
          </button>
          <button
            className="btn-primary mvp-cta-sm"
            onClick={onGenerateSceneImages}
            disabled={disabled || shotCount === 0}
            data-testid={`scene-generate-images-${scene.id}`}
          >
            <ImagePlus size={13} /> Generate Images for Scene
          </button>
        </div>
      </header>

      {scene.summary && <p className="mvp-scene-summary">{scene.summary}</p>}

      {!collapsed && (scene.beats || []).map((beat, bi) => (
        <div key={beat.id} className="mvp-beat-block" data-testid={`beat-${beat.id}`}>
          <div className="mvp-beat-head">
            <span className="mvp-beat-num">Beat {bi + 1}</span>
            <strong>{beat.title}</strong>
            {beat.summary && <span className="mvp-dim mvp-beat-summary">— {beat.summary}</span>}
          </div>
          <div className="mvp-shots-filmstrip">
            {(beat.shots || []).map((shot, shi) => (
              <ShotCard
                key={shot.id}
                shot={shot}
                selected={selection.has(shot.id)}
                onToggleSelect={() => onToggleSelect(shot.id)}
                onPatch={(patch) => onPatchShot(shot.id, patch)}
                onGenerate={(upscale) => onGenerateShot(shot.id, upscale)}
                onDelete={() => onDeleteShot(shot.id)}
                onSplit={() => onSplitShot(shot.id)}
                onAddAfter={() => onAddShot(shot.id)}
                busyGenerating={batchShots.has(shot.id) || shot.image_status === "generating"}
                disabled={disabled}
              />
            ))}
            <button
              className="mvp-shot-add"
              onClick={() => onAddShot()}
              disabled={disabled}
              data-testid={`shot-add-in-scene-${scene.id}`}
            >
              <Plus size={20} />
              <span>Add Shot</span>
            </button>
          </div>
        </div>
      ))}
    </section>
  );
};

// =================== Shot card ===================
const ShotCard = ({
  shot, selected, onToggleSelect, onPatch, onGenerate, onDelete, onSplit, onAddAfter,
  busyGenerating, disabled,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState(shot.image_prompt || "");
  useEffect(() => { setPromptDraft(shot.image_prompt || ""); }, [shot.image_prompt]);

  const hasImage = !!shot.image_url;

  return (
    <div className={`mvp-shot-card ${selected ? "mvp-shot-selected" : ""}`} data-testid={`shot-${shot.id}`}>
      <div className="mvp-shot-topbar">
        <label className="mvp-shot-select">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={disabled}
            data-testid={`shot-select-${shot.id}`}
          />
          <span className="mvp-shot-num">#{shot.shot_number}</span>
        </label>
        <select
          value={shot.shot_type || "character_action"}
          onChange={(e) => onPatch({ shot_type: e.target.value })}
          disabled={disabled}
          className="mvp-shot-select-inline"
          data-testid={`shot-type-${shot.id}`}
        >
          {SHOT_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
        </select>
        <span className="mvp-shot-cam">
          <Camera size={11} />
          <EditableText
            value={shot.camera_angle || ""}
            onChange={(v) => onPatch({ camera_angle: v })}
            testid={`shot-cam-${shot.id}`}
            emptyLabel="angle"
          />
        </span>
      </div>

      <div className="mvp-shot-media" data-testid={`shot-media-${shot.id}`}>
        {shot.image_url ? (
          <a href={shot.image_url} target="_blank" rel="noreferrer">
            <img src={shot.image_url} alt={`Shot ${shot.shot_number}`} data-testid={`shot-image-${shot.id}`} />
          </a>
        ) : (
          <div className={`mvp-shot-empty ${busyGenerating ? "mvp-shot-generating" : ""}`}>
            {busyGenerating ? (
              <><Loader2 size={20} className="mvp-spin" /><span>Illustrating…</span></>
            ) : (
              <><ImageOff size={18} /><span>No image</span></>
            )}
          </div>
        )}
        {shot.image_status === "failed" && !busyGenerating && (
          <div className="mvp-shot-fail"><AlertTriangle size={12} /> {shot.image_error || "Failed"}</div>
        )}
        {shot.image_upscaled && (
          <span className="mvp-shot-upscaled"><Sparkles size={10} /> HD</span>
        )}
      </div>

      <div className="mvp-shot-body">
        <div className="mvp-shot-field">
          <label>Action</label>
          <EditableText
            multiline
            value={shot.action || ""}
            onChange={(v) => onPatch({ action: v })}
            className="mvp-shot-text"
            testid={`shot-action-${shot.id}`}
            emptyLabel="What happens in this shot…"
          />
        </div>
        <div className="mvp-shot-field">
          <label>Emotion</label>
          <EditableText
            value={shot.character_emotion || ""}
            onChange={(v) => onPatch({ character_emotion: v })}
            className="mvp-shot-text"
            testid={`shot-emotion-${shot.id}`}
            emptyLabel="—"
          />
        </div>
        {(shot.characters_in_shot || []).length > 0 && (
          <div className="mvp-shot-chars">
            <Users size={11} /> {shot.characters_in_shot.join(", ")}
          </div>
        )}
        {shot.dialogue !== undefined && (
          <div className="mvp-shot-field">
            <label><MessageCircle size={11} /> Dialogue / Caption</label>
            <EditableText
              multiline
              value={shot.dialogue || ""}
              onChange={(v) => onPatch({ dialogue: v })}
              className="mvp-shot-text mvp-shot-dialogue"
              testid={`shot-dialogue-${shot.id}`}
              emptyLabel="—"
            />
          </div>
        )}
        {shot.continuity_notes !== undefined && (
          <div className="mvp-shot-cont">
            <span className="mvp-shot-cont-label">→ next</span>
            <EditableText
              multiline
              value={shot.continuity_notes || ""}
              onChange={(v) => onPatch({ continuity_notes: v })}
              testid={`shot-continuity-${shot.id}`}
              emptyLabel="Continuity to next panel…"
            />
          </div>
        )}
      </div>

      <div className="mvp-shot-buttons">
        {!hasImage ? (
          <button
            className="btn-primary mvp-cta-sm mvp-shot-btn-primary"
            onClick={() => onGenerate(false)}
            disabled={disabled || busyGenerating}
            data-testid={`shot-generate-${shot.id}`}
          >
            <ImagePlus size={12} /> {busyGenerating ? "Generating…" : "Generate"}
          </button>
        ) : (
          <>
            <button className="mvp-icon-btn" onClick={() => onGenerate(false)} disabled={disabled || busyGenerating} title="Regenerate" data-testid={`shot-regenerate-${shot.id}`}>
              <RefreshCw size={12} />
            </button>
            <button className="mvp-icon-btn" onClick={() => onGenerate(true)} disabled={disabled || busyGenerating} title="Upscale" data-testid={`shot-upscale-${shot.id}`}>
              <Sparkles size={12} />
            </button>
          </>
        )}
        <button className="mvp-icon-btn" onClick={() => setShowPrompt(true)} disabled={disabled} title="Edit prompt" data-testid={`shot-edit-prompt-${shot.id}`}>
          <Pencil size={12} />
        </button>
        <button className="mvp-icon-btn" onClick={onSplit} disabled={disabled} title="Split shot" data-testid={`shot-split-${shot.id}`}>
          <Split size={12} />
        </button>
        <button className="mvp-icon-btn" onClick={onAddAfter} disabled={disabled} title="Add shot after" data-testid={`shot-add-after-${shot.id}`}>
          <Plus size={12} />
        </button>
        <button className="mvp-icon-btn mvp-icon-btn-danger" onClick={onDelete} disabled={disabled} title="Delete shot" data-testid={`shot-delete-${shot.id}`}>
          <X size={12} />
        </button>
      </div>

      {showPrompt && (
        <div className="mvp-modal-backdrop" onClick={() => setShowPrompt(false)}>
          <div className="mvp-modal mvp-modal-wide" onClick={(e) => e.stopPropagation()} data-testid={`shot-prompt-modal-${shot.id}`}>
            <button className="mvp-modal-close" onClick={() => setShowPrompt(false)}><X size={16} /></button>
            <h3>Shot #{shot.shot_number} prompt</h3>
            <p>Camera, action, emotion, and character appearance are auto-appended. This is the base description that goes to Nano Banana.</p>
            <textarea
              className="mvp-json-area mvp-prompt-area"
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              rows={6}
              data-testid={`shot-prompt-textarea-${shot.id}`}
            />
            <div className="mvp-modal-actions">
              <button className="mvp-nav-ghost" onClick={() => setShowPrompt(false)} data-testid={`shot-prompt-cancel-${shot.id}`}>
                Cancel
              </button>
              <button
                className="mvp-nav-ghost"
                onClick={async () => { await onPatch({ image_prompt: promptDraft }); setShowPrompt(false); }}
                data-testid={`shot-prompt-save-${shot.id}`}
              >
                <Check size={13} /> Save
              </button>
              <button
                className="btn-primary mvp-cta-sm"
                onClick={async () => { await onPatch({ image_prompt: promptDraft }); setShowPrompt(false); onGenerate(false); }}
                data-testid={`shot-prompt-regenerate-${shot.id}`}
              >
                <Wand2 size={13} /> Save & Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =================== Reorder modal ===================
const ReorderModal = ({ scene, list, onMove, onApply, onCancel }) => (
  <div className="mvp-modal-backdrop" onClick={onCancel}>
    <div className="mvp-modal mvp-modal-wide" onClick={(e) => e.stopPropagation()} data-testid={`reorder-modal-${scene.id}`}>
      <button className="mvp-modal-close" onClick={onCancel}><X size={16} /></button>
      <h3>Reorder shots — {scene.title}</h3>
      <p>Move shots up or down to change the sequence. Existing beat groupings are preserved by size.</p>
      <ul className="mvp-reorder-list">
        {list.map((shot, i) => (
          <li key={shot.id} className="mvp-reorder-item" data-testid={`reorder-item-${shot.id}`}>
            <span className="mvp-reorder-num">#{shot.shot_number}</span>
            <span className="mvp-reorder-info">
              <span className="mvp-chip">{shot.shot_type}</span>
              <span className="mvp-dim mvp-reorder-action">{(shot.action || "").slice(0, 90)}</span>
            </span>
            <div className="mvp-reorder-btns">
              <button className="mvp-icon-btn" onClick={() => onMove(i, -1)} disabled={i === 0} data-testid={`reorder-up-${shot.id}`}>
                <ChevronUp size={14} />
              </button>
              <button className="mvp-icon-btn" onClick={() => onMove(i, 1)} disabled={i === list.length - 1} data-testid={`reorder-down-${shot.id}`}>
                <ChevronDown size={14} />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <div className="mvp-modal-actions">
        <button className="mvp-nav-ghost" onClick={onCancel} data-testid="reorder-cancel">Cancel</button>
        <button className="btn-primary mvp-cta-sm" onClick={onApply} data-testid="reorder-apply">
          <Check size={13} /> Apply
        </button>
      </div>
    </div>
  </div>
);
